import { llmCourtCasesOpinionsSummary } from '@ltvco/refresh-lib/api';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useServiceToken, useSnackbar } from '@ltvco/refresh-lib/v1';
import { useQuery } from '@ltvco/refresh-lib/vendors';
import { useState, useEffect, useRef, useContext } from 'react';

import {
  type OpinionsResponse,
  type PACERResponse,
  type CourtCaseCluster,
  type CourtDocket,
  type Court,
  type CourtOpinion,
  type SearchResults,
  DocketEntriesResponse,
  type AttorneysResponse,
  type PartiesResponse,
  type Attorney,
  type Party,
  type DisplayOpinion,
  type JudgeSearchResponse,
  type ClassActionSearchResponse,
  type OralArgumentsResponse,
  type CourtTypeParam,
} from './types';
import {
  getOrderByParam,
  updateUrlParams,
  normalizeAdvancedFilters,
  buildCourtSearchParams,
  analyzeSearchStrategy,
  processExactMatchQuery,
  prepareQueryForCacheKey,
} from './utils';

// ===================== Constants =====================
export const API_CONFIG = {
  BASE_URL: '/oram',
  CACHE_TIME: 10 * 60 * 1000, // 10 minutes
  STALE_TIME: 2 * 60 * 1000, // 2 minutes
  MAX_RETRIES: 2,
};

// Common query options
export const DEFAULT_QUERY_OPTIONS = {
  staleTime: API_CONFIG.STALE_TIME,
  cacheTime: API_CONFIG.CACHE_TIME,
  refetchOnWindowFocus: false,
};

export const ERROR_MESSAGES = {
  TOKEN_UNAVAILABLE:
    'Service token not available. Please wait for authentication to complete.',
  TOKEN_REFRESH_FAILED: 'Failed to refresh service token',
  OPINIONS_SEARCH_FAILED: 'Failed to search court opinions',
  CASES_SEARCH_FAILED: 'Failed to search court cases',
  ORAL_ARGUMENTS_SEARCH_FAILED: 'Failed to search oral arguments',
  BOTH_SEARCHES_FAILED: 'Unable to search court records. Please try again.',
  GENERAL_ERROR:
    'Something went wrong while loading results. Please try again.',
} as const;

// ===================== Utility Functions =====================
/**
 * Makes an authenticated GET request to the API
 * @param url - API endpoint URL
 * @param token - Authentication token
 * @returns Promise with response data
 */
export const makeAuthenticatedGetRequest = async <T>(
  url: string,
  token: string
): Promise<T> => {
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

const processSearchResults = (
  results: PromiseSettledResult<OpinionsResponse | PACERResponse>[],
  searchStrategy: { includeOpinions: boolean; includeCases: boolean }
): {
  opinionsResult: PromiseSettledResult<OpinionsResponse | null>;
  pacerResult: PromiseSettledResult<PACERResponse | null>;
} => {
  if (!searchStrategy.includeOpinions) {
    return {
      opinionsResult: { status: 'fulfilled', value: null },
      pacerResult: results[0] as PromiseSettledResult<PACERResponse>,
    };
  } else if (!searchStrategy.includeCases) {
    return {
      opinionsResult: results[0] as PromiseSettledResult<OpinionsResponse>,
      pacerResult: { status: 'fulfilled', value: null },
    };
  }
  return {
    opinionsResult: results[0] as PromiseSettledResult<OpinionsResponse>,
    pacerResult: results[1] as PromiseSettledResult<PACERResponse>,
  };
};

/**
 * Resets a search reference after a specified delay
 * @param ref - Reference to reset
 * @param delay - Delay in milliseconds
 */
export const resetSearchRefAfterDelay = (
  ref: React.MutableRefObject<boolean>,
  delay: number = 100
) => {
  setTimeout(() => {
    ref.current = false;
  }, delay);
};

/**
 * Creates a delay promise
 * @param ms - Milliseconds to delay
 * @returns Promise that resolves after the delay
 */
export const createDelay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// ===================== Factory Functions =====================
/**
 * Creates a search function for either opinions, cases, or oral arguments
 * @param searchType - 'o' for opinions, 'r' for cases (PACER), 'oa' for oral arguments, 'p' for judges/people
 * @param makeApiCall - Function to make authenticated API calls
 * @returns Search function that accepts query and parameters
 */
export const createSearchFunction = <
  T extends
    | OpinionsResponse
    | PACERResponse
    | OralArgumentsResponse
    | JudgeSearchResponse
>(
  searchType: CourtTypeParam,
  makeApiCall: (apiCall: (token: string) => Promise<T>) => Promise<T>
) => {
  return async (
    query: string,
    orderBy: string = 'score desc',
    additionalParams: Record<string, string | number | boolean> = {}
  ): Promise<T> => {
    return makeApiCall(async (token: string) => {
      const searchParams = buildCourtSearchParams(
        searchType,
        query,
        orderBy,
        additionalParams
      );
      const url = `${API_CONFIG.BASE_URL}/legal/search/?${searchParams}`;
      return makeAuthenticatedGetRequest<T>(url, token);
    });
  };
};

// ===================== Core API Hook =====================
/**
 * Core hook that provides authenticated API methods for court cases
 * @returns API methods and authentication state
 */
export const useCourtCasesApi = () => {
  const {
    data: tokenData,
    refetch: refetchToken,
    isLoading: isTokenLoading,
  } = useServiceToken('oram');
  const { logError } = useContext(AppConfig);

  // Handles API calls with token refresh logic
  const makeApiCall = <T>(
    apiCall: (token: string) => Promise<T>,
    retryCount = 0
  ): Promise<T> => {
    if (!tokenData?.token) {
      throw new Error(ERROR_MESSAGES.TOKEN_UNAVAILABLE);
    }
    return apiCall(tokenData.token).catch(async (error: unknown) => {
      if (
        error instanceof Error &&
        error.message?.includes('401') &&
        retryCount === 0
      ) {
        try {
          await refetchToken();
          await createDelay(100);
          return makeApiCall(apiCall, retryCount + 1);
        } catch {
          throw new Error(ERROR_MESSAGES.TOKEN_REFRESH_FAILED);
        }
      }
      throw error;
    });
  };

  // API Methods
  const searchCaseLaw = createSearchFunction<OpinionsResponse>(
    'o',
    makeApiCall
  );
  const searchPACER = createSearchFunction<PACERResponse>('r', makeApiCall);

  /**
   * Fetches a court case cluster by ID
   * @param id - Cluster ID
   * @returns Court case cluster or null if not found
   */
  const getCluster = async (id: string): Promise<CourtCaseCluster | null> => {
    return makeApiCall(async (token: string) => {
      const url = `${API_CONFIG.BASE_URL}/legal/clusters/?id=${id}`;
      const data = await makeAuthenticatedGetRequest<any>(url, token);
      return data.results?.[0] || null;
    });
  };

  /**
   * Fetches opinions for a specific court case cluster
   * @param clusterId - Cluster ID
   * @returns Array of court opinions
   */
  const getClusterOpinions = async (
    clusterId: string
  ): Promise<CourtOpinion[]> => {
    return makeApiCall(async (token: string) => {
      const url = `${API_CONFIG.BASE_URL}/legal/opinions/?cluster=${clusterId}`;
      const data = await makeAuthenticatedGetRequest<any>(url, token);
      return data.results || [];
    });
  };

  /**
   * Fetches a court docket by ID
   * @param id - Docket ID
   * @returns Court docket or null if not found
   */
  const getDocket = async (id: string): Promise<CourtDocket | null> => {
    return makeApiCall(async (token: string) => {
      const url = `${API_CONFIG.BASE_URL}/legal/dockets/?id=${id}`;
      const data = await makeAuthenticatedGetRequest<any>(url, token);
      return data.results?.[0] || null;
    });
  };

  /**
   * Fetches court information by ID
   * @param id - Court ID
   * @returns Court information
   */
  const getCourt = async (id: string): Promise<Court> => {
    return makeApiCall(async (token: string) => {
      const url = `${API_CONFIG.BASE_URL}/legal/courts/?id=${id}`;
      const data = await makeAuthenticatedGetRequest<any>(url, token);
      return data.results?.[0] || null;
    });
  };

  /**
   * Fetches a pagination page from a URL with configurable endpoint
   * @param url - Full pagination URL
   * @param endpoint - API endpoint path (e.g., 'search', 'attorneys', 'parties')
   * @returns Paginated response data
   */
  const fetchPaginationPage = <
    T extends
      | OpinionsResponse
      | PACERResponse
      | AttorneysResponse
      | PartiesResponse
      | OralArgumentsResponse
      | JudgeSearchResponse
      | ClassActionSearchResponse
  >(
    url: string,
    endpoint: string = 'search'
  ): Promise<T> => {
    return makeApiCall(async (token: string) => {
      const urlObj = new URL(url);
      const oramUrl = `${API_CONFIG.BASE_URL}/legal/${endpoint}/${urlObj.search}`;
      return makeAuthenticatedGetRequest<T>(oramUrl, token);
    });
  };

  /**
   * Fetches court docket entries by docket
   * @param docket - Docket ID
   * @param page - Optional page number for pagination
   * @returns Court docket entries response with pagination
   */
  const getDocketEntries = async (
    docket: string,
    page?: number
  ): Promise<any> => {
    return makeApiCall(async (token: string) => {
      let url = `${API_CONFIG.BASE_URL}/legal/docket-entries/?docket=${docket}&order_by=recap_sequence_number`;

      // Add page parameter if provided
      if (page) {
        url += `&page=${page}`;
      }

      const data = await makeAuthenticatedGetRequest<any>(url, token);
      return data;
    });
  };

  /*Validates and sanitizes various types of IDs used in API calls*/
  const validateId = (id: string, type: string = 'ID'): string => {
    const trimmedId = id.trim();

    if (!trimmedId) {
      throw new Error(`${type} cannot be empty`);
    }

    if (!/^[a-zA-Z0-9\-_.:]+$/.test(trimmedId)) {
      throw new Error(`${type} contains invalid characters`);
    }

    return trimmedId;
  };

  const validateEndpoint = (endpoint: string): string => {
    const trimmedEndpoint = endpoint.trim();

    if (!trimmedEndpoint) {
      throw new Error('Endpoint cannot be empty');
    }

    // Only allow specific known endpoints
    const allowedEndpoints = [
      'attorneys',
      'parties',
      'docket-entries',
      'search',
      'clusters',
      'opinions',
      'dockets',
      'courts',
    ];

    if (!allowedEndpoints.includes(trimmedEndpoint)) {
      throw new Error(`Invalid endpoint: ${trimmedEndpoint}`);
    }

    return trimmedEndpoint;
  };

  /** Validates pagination URLs*/
  const isValidNextUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);

      if (urlObj.protocol !== 'https:') {
        return false;
      }

      if (
        url.includes('<') ||
        url.includes('>') ||
        url.includes('"') ||
        url.includes("'")
      ) {
        return false;
      }
    } catch {
      return false;
    }
    return true;
  };

  /**
   * Generic helper function to fetch all paginated data
   * @param endpoint - The API endpoint path (e.g., 'attorneys', 'parties')
   * @param docketId - Docket ID
   * @returns Array of all items from all pages
   */
  const getAllPaginatedData = async <
    T,
    R extends { results: T[]; next: string | null }
  >(
    endpoint: string,
    docketId: string
  ): Promise<T[]> => {
    return makeApiCall(async (token: string) => {
      const validatedEndpoint = validateEndpoint(endpoint);
      const validatedDocketId = validateId(docketId, 'Docket ID');

      const maxPages = 25; // Maximum number of pages to fetch to prevent infinite loops
      let allItems: T[] = [];

      // Use URLSearchParams for proper encoding
      const searchParams = new URLSearchParams({ docket: validatedDocketId });
      let nextUrl: string | null = `${
        API_CONFIG.BASE_URL
      }/legal/${validatedEndpoint}?${searchParams.toString()}`;
      let pageCount = 0;

      while (pageCount < maxPages && nextUrl) {
        const response: R = await makeAuthenticatedGetRequest<R>(
          nextUrl,
          token
        );
        allItems = [...allItems, ...response.results];
        pageCount++;

        //Validate pagination URLs before using them
        if (response.next) {
          if (isValidNextUrl(response.next)) {
            const urlObj = new URL(response.next);
            nextUrl = `${API_CONFIG.BASE_URL}/legal/${validatedEndpoint}${urlObj.search}`;
          } else {
            nextUrl = null; // Invalid URL, stop pagination
          }
        } else {
          nextUrl = null; // No more pages
        }
      }

      if (pageCount >= maxPages) {
        logError(
          `getAllPaginatedData for ${endpoint} reached maximum page limit (${maxPages}) for docket ${docketId}`,
          new Error(`Pagination limit exceeded for ${endpoint}`)
        );
      }

      return allItems;
    });
  };

  /**
   * Fetches all attorneys for a specific docket by following pagination
   * @param docketId - Docket ID
   * @returns Array of all attorneys for the docket
   */
  const getAllAttorneys = async (docketId: string): Promise<Attorney[]> => {
    return getAllPaginatedData<Attorney, AttorneysResponse>(
      'attorneys',
      docketId
    );
  };

  /**
   * Fetches all parties for a specific docket by following pagination
   * @param docketId - Docket ID
   * @returns Array of all parties for the docket
   */
  const getAllParties = async (docketId: string): Promise<Party[]> => {
    return getAllPaginatedData<Party, PartiesResponse>('parties', docketId);
  };

  return {
    searchCaseLaw,
    searchPACER,
    getCluster,
    getClusterOpinions,
    getDocket,
    getDocketEntries,
    getAllAttorneys,
    getAllParties,
    getCourt,
    fetchPaginationPage,
    isLoading: isTokenLoading,
    tokenData,
  };
};

// ===================== Main Search Hook =====================
/**
 * Main hook for court case search functionality
 * @param initialQuery - Initial search query string
 * @param exactMatch - Whether to use exact match search mode
 * @param initialTab - Initial tab to show (0 = Cases, 1 = Opinions). If provided, prevents auto-switching on search completion.
 * @returns Search state and functions for court case searches
 */
export const useCourtSearch = (
  initialQuery: string = '',
  exactMatch: boolean = false,
  initialTab?: number
) => {
  // State
  const [activeTab, setActiveTab] = useState(initialTab ?? 0);
  // Track if user explicitly requested a tab via URL to prevent auto-switching
  const hasExplicitTab = initialTab !== undefined;
  const [sortOption, setSortOption] = useState('score desc');
  const [currentQuery, setCurrentQuery] = useState('');
  const [currentFilters, setCurrentFilters] = useState<
    Record<string, string | number | boolean>
  >({});
  const [opinionsResults, setOpinionsResults] =
    useState<OpinionsResponse | null>(null);
  const [pacerResults, setPacerResults] = useState<PACERResponse | null>(null);
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [currentExactMatch, setCurrentExactMatch] = useState(exactMatch);

  // Refs
  const isSearchingRef = useRef(false);
  const isUpdatingStateRef = useRef(false);

  // Contexts
  const { enqueueSnackbar } = useSnackbar();
  const { logError } = useContext(AppConfig);
  const {
    searchCaseLaw,
    searchPACER,
    isLoading: isTokenLoading,
    tokenData,
  } = useCourtCasesApi();

  // Computed values
  const hasAnyFilters = Object.keys(currentFilters || {}).length > 0;
  const hasValidSearchParams = Boolean(
    Boolean(currentQuery && currentQuery.trim().length > 0) || hasAnyFilters
  );
  const shouldExecuteQuery = Boolean(
    searchExecuted && hasValidSearchParams && !isUpdatingStateRef.current
  );

  // Error Handler
  const handleSearchError = (error: unknown, context: string) => {
    if (
      error instanceof Error &&
      !error.message?.includes('Search query is required')
    ) {
      logError(`Court search error in ${context}`, error);
      enqueueSnackbar(ERROR_MESSAGES.GENERAL_ERROR, { variant: 'error' });
    }
  };

  const executeSearch = async (): Promise<SearchResults> => {
    const orderBy = getOrderByParam(sortOption);
    const searchStrategy = analyzeSearchStrategy(currentFilters);

    // Process query based on exact match setting with validation
    // Using the same utility as for query key generation to ensure consistency
    const processedQuery = currentQuery.trim()
      ? processExactMatchQuery(currentQuery, currentExactMatch)
      : currentQuery;

    // Build search promises based on strategy
    const searchPromises: Promise<OpinionsResponse | PACERResponse>[] = [];

    if (searchStrategy.includeOpinions) {
      searchPromises.push(
        searchCaseLaw(processedQuery, orderBy, currentFilters)
      );
    }
    if (searchStrategy.includeCases) {
      searchPromises.push(searchPACER(processedQuery, orderBy, currentFilters));
    }

    // Execute searches and process results
    const results = await Promise.allSettled(searchPromises);
    const { opinionsResult, pacerResult } = processSearchResults(
      results,
      searchStrategy
    );

    // Initialize result variables
    let opinions: OpinionsResponse | null = null;
    let pacer: PACERResponse | null = null;
    let hasAnySuccess = false;

    // Process opinions result
    if (searchStrategy.includeOpinions) {
      if (opinionsResult.status === 'fulfilled') {
        opinions = opinionsResult.value;
        hasAnySuccess = true;
      } else {
        logError(
          'Failed to search court opinions',
          opinionsResult.reason as Error
        );
        enqueueSnackbar(ERROR_MESSAGES.OPINIONS_SEARCH_FAILED, {
          variant: 'error',
        });
      }
    }

    // Process PACER result
    if (pacerResult.status === 'fulfilled') {
      pacer = pacerResult.value;
      hasAnySuccess = true;
    } else {
      logError('Failed to search court cases', pacerResult.reason as Error);
      enqueueSnackbar(ERROR_MESSAGES.CASES_SEARCH_FAILED, {
        variant: 'error',
      });
    }

    // Validate we have at least one successful result
    if (!hasAnySuccess) {
      enqueueSnackbar(ERROR_MESSAGES.BOTH_SEARCHES_FAILED, {
        variant: 'error',
      });
      throw new Error('Both searches failed');
    }

    return { opinions, pacer };
  };

  const filtersKey =
    Object.keys(currentFilters).length > 0
      ? JSON.stringify(currentFilters)
      : 'no-filters';

  // Success handler for search query
  const handleSearchSuccess = (data: SearchResults) => {
    setOpinionsResults(data.opinions);
    setPacerResults(data.pacer);
    updateUrlParams({ query: currentQuery.trim() });
    setSearchExecuted(false);

    // Tab 0 = Cases (PACER), Tab 1 = Opinions
    // Only auto-switch tabs if user didn't explicitly request a tab via URL
    if (!hasExplicitTab) {
      // Show the tab that has results, prioritizing Cases
      if (data.pacer?.count && data.pacer.count > 0) {
        setActiveTab(0); // Show Cases tab
      } else if (data.opinions?.count && data.opinions.count > 0) {
        setActiveTab(1); // Show Opinions tab
      }
    }
  };

  // Error handler for search query
  const handleSearchQueryError = (error: unknown) => {
    handleSearchError(error, 'search query execution');
    setSearchExecuted(false);
    setOpinionsResults(null);
    setPacerResults(null);
  };

  // Create the query key with exactMatch to ensure cache invalidation when it changes
  const queryKeyQuery = prepareQueryForCacheKey(
    currentQuery,
    currentExactMatch
  );

  const queryKey = ['court-search', queryKeyQuery, sortOption, filtersKey];

  // Main Search Query
  const searchQuery = useQuery(queryKey, executeSearch, {
    enabled: shouldExecuteQuery && !isTokenLoading && !!tokenData?.token,
    staleTime: API_CONFIG.STALE_TIME,
    cacheTime: API_CONFIG.CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount: number, error: unknown) => {
      if (
        error instanceof Error &&
        error.message?.includes('Search query is required')
      ) {
        return false;
      }
      return failureCount < API_CONFIG.MAX_RETRIES;
    },
    onSuccess: handleSearchSuccess,
    onError: handleSearchQueryError,
  });

  // Update exact match state when prop changes and force a new search if needed
  useEffect(() => {
    if (currentExactMatch !== exactMatch) {
      setCurrentExactMatch(exactMatch);

      // If there's an active search query, force a refetch with the new exactMatch setting
      if (currentQuery.trim() && searchQuery.data) {
        searchQuery.refetch();
      }
    }
  }, [exactMatch, currentQuery, currentExactMatch, searchQuery]);

  // Effects
  useEffect(() => {
    if (initialQuery && initialQuery.trim()) {
      isUpdatingStateRef.current = true;
      setCurrentQuery(initialQuery);
      setTimeout(() => {
        isUpdatingStateRef.current = false;
        setSearchExecuted(true);
      }, 0);
    }
  }, [initialQuery]);

  useEffect(() => {
    if (searchQuery.data && !searchQuery.isLoading) {
      setOpinionsResults(searchQuery.data.opinions);
      setPacerResults(searchQuery.data.pacer);
    }
  }, [searchQuery.data, searchQuery.isLoading]);

  // Actions
  const triggerSearch = (query: string) => {
    if (query.trim()) {
      isSearchingRef.current = true;
      isUpdatingStateRef.current = true;
      setCurrentQuery(query);
      setTimeout(() => {
        isUpdatingStateRef.current = false;
        setSearchExecuted(true);
      }, 0);
      resetSearchRefAfterDelay(isSearchingRef);
    }
  };

  const updateSortOption = (newSortOption: string) => {
    setSortOption(newSortOption);
  };

  const updateAdvancedFilters = (filters: Record<string, unknown> = {}) => {
    const apiReadyFilters = normalizeAdvancedFilters(filters);
    setCurrentFilters(
      apiReadyFilters as Record<string, string | number | boolean>
    );
  };

  const triggerCombinedSearch = (
    query: string,
    filters: Record<string, unknown> = {}
  ) => {
    isSearchingRef.current = true;
    const apiReadyFilters = normalizeAdvancedFilters(filters);
    isUpdatingStateRef.current = true;
    setCurrentFilters(
      apiReadyFilters as Record<string, string | number | boolean>
    );

    if (
      apiReadyFilters.order_by &&
      typeof apiReadyFilters.order_by === 'string' &&
      apiReadyFilters.order_by !== sortOption
    ) {
      setSortOption(apiReadyFilters.order_by);
    }

    setCurrentQuery(query);
    setTimeout(() => {
      isUpdatingStateRef.current = false;
      setSearchExecuted(true);
    }, 0);
    resetSearchRefAfterDelay(isSearchingRef);
  };

  const updatePaginationResults = <T extends OpinionsResponse | PACERResponse>(
    newResults: T,
    setResults: (results: T) => void
  ) => {
    setResults(newResults);
    setIsPaginationLoading(false);
  };

  const updateOpinionsResults = (newResults: OpinionsResponse) => {
    updatePaginationResults(newResults, setOpinionsResults);
  };

  const updatePacerResults = (newResults: PACERResponse) => {
    updatePaginationResults(newResults, setPacerResults);
  };

  const setPaginationLoading = (loading: boolean) => {
    setIsPaginationLoading(loading);
  };

  // Computed values
  const isSearching = searchExecuted && searchQuery.isLoading;
  const isAnyLoading = isSearching || isPaginationLoading;
  const hasResults = !!(opinionsResults || pacerResults);
  const hasError = !!searchQuery.error;
  const generalError =
    searchQuery.error &&
    !(
      searchQuery.error instanceof Error &&
      searchQuery.error.message?.includes('Search query is required')
    )
      ? ERROR_MESSAGES.GENERAL_ERROR
      : null;

  return {
    // Data
    opinionsResults,
    pacerResults,
    searchQuery: currentQuery,
    activeTab,
    sortOption,

    // States
    isSearching,
    isAnyLoading,
    hasResults,
    hasError,
    generalError,
    isTokenLoading,
    isPaginationLoading,

    // Actions
    triggerSearch,
    triggerCombinedSearch,
    setActiveTab,
    setSortOption: updateSortOption,
    updateAdvancedFilters,
    updateOpinionsResults,
    updatePacerResults,
    setPaginationLoading,
    setSearchExecuted,

    // React-query helpers
    error: searchQuery.error,
    data: searchQuery.data,
    refetch: searchQuery.refetch,
  };
};

/**
 * Custom hook for fetching court case details
 * @param id - Court case ID
 * @returns Court case details query results
 */
export const useCourtCaseDetails = (id: string | undefined) => {
  const { getDocket, getCourt, isLoading: isTokenLoading } = useCourtCasesApi();

  // Query for docket information
  const docketQuery = useQuery(
    ['docket', id],
    () => (id ? getDocket(id) : null),
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!id && !isTokenLoading,
    }
  );

  // Query for court information
  const courtQuery = useQuery(
    ['court', docketQuery.data?.court_id],
    () =>
      docketQuery.data?.court_id ? getCourt(docketQuery.data.court_id) : null,
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!docketQuery.data?.court_id && !isTokenLoading,
    }
  );

  return {
    docketQuery,
    courtQuery,
    isLoading: docketQuery.isLoading || isTokenLoading,
    isError: docketQuery.isError,
    error: docketQuery.error,
    data: docketQuery.data,
  };
};

/**
 * Custom hook for fetching court opinion details
 * @param id - Court opinion ID
 * @returns Court opinion details query results with related data
 */
export const useCourtOpinionDetails = (id: string | undefined) => {
  const {
    getCluster,
    getClusterOpinions,
    searchCaseLaw,
    isLoading: isTokenLoading,
  } = useCourtCasesApi();

  // Query for cluster information
  const clusterQuery = useQuery(
    ['cluster', id],
    () => (id ? getCluster(id) : null),
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!id && !isTokenLoading,
    }
  );

  // Query for cluster opinions
  const opinionsQuery = useQuery(
    ['cluster-opinions', id],
    async () => {
      if (!id || !clusterQuery.data) {
        return [];
      }

      const opinions = await getClusterOpinions(id);

      // Add the cluster's headmatter as the first opinion
      const opinionsWithHeadmatter = clusterQuery.data.headmatter
        ? [
            {
              id: -1, // Use negative ID to distinguish from real opinions
              type: 'headmatter' as const,
              html_with_citations: clusterQuery.data.headmatter,
              isHeadmatter: true as const,
              meta: {
                date_created: clusterQuery.data.date_created,
              },
            },
            ...opinions,
          ]
        : opinions;

      return opinionsWithHeadmatter;
    },
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!id && !!clusterQuery.data && !isTokenLoading,
    }
  );

  // Get opinion IDs for similar cases query
  const opinionIds = opinionsQuery.data
    ?.filter((opinion) => opinion.type !== 'headmatter')
    .map((opinion) => opinion.id)
    .join(',');

  // Query for similar cases
  const similarCasesQuery = useQuery(
    ['similar-cases', opinionIds, clusterQuery.data?.docket_id],
    async () => {
      if (!clusterQuery.data) {
        return null;
      }

      const relatedQuery = opinionIds
        ? `related:${opinionIds}`
        : `related:${clusterQuery.data.docket_id}`;

      const results = await searchCaseLaw(relatedQuery);

      // Limit to first 5 results
      return {
        ...results,
        results: results.results.slice(0, 5),
      };
    },
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!clusterQuery.data && !isTokenLoading,
    }
  );

  // Get display opinion IDs (excluding headmatter)
  const displayOpinionIds =
    opinionsQuery.data
      ?.filter((opinion) => opinion.type !== 'headmatter')
      .map((opinion) => opinion.id)
      .join(',') || '';

  return {
    clusterQuery,
    opinionsQuery,
    similarCasesQuery,
    displayOpinionIds,
    isLoading: clusterQuery.isLoading || isTokenLoading,
    isError: clusterQuery.isError,
    error: clusterQuery.error,
    data: clusterQuery.data,
  };
};

export const DOCKET_ENTRIES_QUERY_OPTIONS = {
  retry: 3, // Number of retries
  retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30000), // Exponential backoff with 30s max
  staleTime: 2 * 60 * 1000, // 2 minutes
  cacheTime: 10 * 60 * 1000, // 10 minutes
  refetchOnWindowFocus: false,
};

/**
 * Custom hook for fetching docket entries data with pagination support
 * @param docketId - The ID of the docket to fetch entries for
 * @param page - Optional page number for pagination
 * @returns Query result with docket entries data and loading state
 */
export const useDocketEntriesData = (docketId: string, page: number = 1) => {
  const { getDocketEntries, isLoading: isTokenLoading } = useCourtCasesApi();
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);

  // Use React Query to handle API calls with built-in caching and retry logic
  const {
    data: docketEntriesData,
    isLoading,
    error,
    isFetching,
  } = useQuery<DocketEntriesResponse>(
    ['docket-entries', docketId, page],
    async () => {
      if (!docketId) {
        return { count: 0, next: null, previous: null, results: [] };
      }
      return getDocketEntries(docketId, page);
    },
    {
      ...DOCKET_ENTRIES_QUERY_OPTIONS,
      enabled: !!docketId && !isTokenLoading,
      keepPreviousData: true, // Keep previous data while fetching new page
      onError: (err: unknown) => {
        console.error('Error fetching docket entries:', err);
      },
    }
  );

  // Update pagination loading state based on isFetching
  useEffect(() => {
    setIsPaginationLoading(isFetching && !isLoading);
  }, [isFetching, isLoading]);

  const docketEntries = docketEntriesData?.results || [];

  return {
    docketEntries,
    effectiveDocketEntries: docketEntriesData,
    isLoading: isLoading || isTokenLoading,
    isPaginationLoading,
    error,
    currentPage: page,
  };
};

/**
 * Custom hook for fetching parties and attorneys data
 * @param docketId - The ID of the docket to fetch parties and attorneys for
 * @returns Query result with parties and attorneys data and loading state
 */
export const usePartiesAttorneysData = (docketId: string) => {
  const {
    getAllAttorneys,
    getAllParties,
    isLoading: isTokenLoading,
  } = useCourtCasesApi();

  // Load all attorneys data
  const attorneysQuery = useQuery<Attorney[]>(
    ['all-attorneys', docketId],
    () => getAllAttorneys(docketId),
    {
      ...DOCKET_ENTRIES_QUERY_OPTIONS,
      enabled: !!docketId && !isTokenLoading,
    }
  );

  // Load all parties data
  const partiesQuery = useQuery<Party[]>(
    ['all-parties', docketId],
    () => getAllParties(docketId),
    {
      ...DOCKET_ENTRIES_QUERY_OPTIONS,
      enabled: !!docketId && !isTokenLoading,
    }
  );

  const isLoading =
    attorneysQuery.isLoading || partiesQuery.isLoading || isTokenLoading;
  const hasError = attorneysQuery.isError || partiesQuery.isError;
  const error = attorneysQuery.error || partiesQuery.error;
  const attorneysData = attorneysQuery.data || [];
  const partiesData = partiesQuery.data || [];

  return {
    attorneysData,
    partiesData,
    isLoading,
    hasError,
    error,
    partiesCount: partiesData.length,
    attorneysCount: attorneysData.length,
  };
};

/**
 * Custom hook for fetching court case opinion summary using LLM
 * @param opinion - The opinion to summarize
 * @param enabled - Whether the query should be enabled
 * @returns Query result with summary data and loading state
 */
export const useCourtCaseOpinionsSummary = (
  opinion: DisplayOpinion | null,
  enabled: boolean = false
) => {
  const { data: tokenData, isLoading: isTokenLoading } =
    useServiceToken('oram');
  const { logError } = useContext(AppConfig);

  const opinionText = opinion?.plain_text || opinion?.html_with_citations || '';
  const opinionId = opinion?.id;

  return useQuery({
    queryKey: ['courtCasesOpinionsSummary', opinionId],
    queryFn: async () => {
      if (!tokenData?.token) {
        const error = new Error('Token not available');
        logError(
          'Failed to generate opinion summary: Token not available',
          error
        );
        throw error;
      }
      const params = { document: opinionText };
      return await llmCourtCasesOpinionsSummary.createCourtCasesOpinionsSummary(
        tokenData.token,
        params
      );
    },
    enabled:
      enabled &&
      Boolean(opinionText) &&
      Boolean(tokenData?.token) &&
      !isTokenLoading,
    ...DEFAULT_QUERY_OPTIONS,
  });
};
